/**
 * 列表展示模式
 *
 * 特点：
 * - 水平布局
 * - 信息密度高
 * - 适合快速浏览
 * - 显示更多元信息
 */

import {
  Check,
  Folder,
  Inbox,
  Pencil,
  Trash2,
} from "lucide-react";
import { memo } from "react";
import type { MouseEventHandler } from "react";

import { getFolderNameById, getItemTagNames, getSourceName, useFolders } from "@/modules/mine";
import { getTextNotePlainText } from "@/modules/mine/text-content";
import type { Item } from "@/modules/mine/types";
import { formatFullDateTime, formatRelativeTime } from "@/shared/lib/date";

import { getItemTypeConfig } from "./display-helpers";

interface ItemRowProps {
  item: Item;
  onClick?: MouseEventHandler<HTMLDivElement>;
  isActive?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onStartSelection?: MouseEventHandler<HTMLButtonElement>;
  onEdit?: () => void;
  onDelete?: () => void;
}

function getWebsiteHostname(url?: string) {
  if (!url) {
    return "website";
  }

  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return getSourceName(url);
  }
}

export const ItemRow = memo(({
    item,
    onClick,
    isActive,
    selectionMode,
    isSelected,
    onStartSelection,
    onEdit,
    onDelete,
  }: ItemRowProps) => {
  // 获取文件夹列表
  const folders = useFolders();
  const itemFormat = item.format ?? "website";
  const typeConfig = getItemTypeConfig(itemFormat);
  const TypeIcon = typeConfig.icon;
  const itemTags = getItemTagNames(item.tags);
  const notePreview = itemFormat === "text"
    ? getTextNotePlainText(item.note)
    : item.note;

  const actionButtonClass = "flex size-7 items-center justify-center rounded-md bg-muted/80 text-muted-foreground backdrop-blur-md transition-colors hover:bg-muted hover:text-foreground";

  const ActionOverlay = () => (
    !onEdit && !onDelete && !onStartSelection ? null : (
      <div
        className="absolute right-3 top-3 z-30 flex items-center gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        {onEdit && (
          <button
            type="button"
            className={actionButtonClass}
            title="编辑"
            onClick={onEdit}
          >
            <Pencil size={13} strokeWidth={2.5} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className={`${actionButtonClass} hover:text-red-600`}
            title="删除"
            onClick={onDelete}
          >
            <Trash2 size={13} strokeWidth={2.5} />
          </button>
        )}
        {onStartSelection && (
          <button
            type="button"
            aria-label="批量选择"
            className="flex size-7 items-center justify-center"
            title="批量选择"
            onClick={onStartSelection}
          >
            <span className="flex size-5 items-center justify-center rounded-md border-[1.5px] border-border/70 bg-background/90 text-transparent">
              <Check className="size-3" strokeWidth={2.6} />
            </span>
          </button>
        )}
      </div>
    )
  );

  // Thumbnail Logic (Right Column)
  const renderThumbnail = () => {
    const containerClass =
      "relative ml-3 hidden h-[72px] w-[104px] shrink-0 overflow-hidden rounded-lg bg-muted/30 md:block";

    if (itemFormat === "image") {
      return (
        <div className={containerClass}>
          <img
            src={item?.url}
            alt={item?.title}
            className="h-full w-full object-cover"
          />
        </div>
      );
    }

    if (itemFormat === "website") {
      if (item.website?.cover_url) {
        return (
          <div className={containerClass}>
            <img
              src={item.website.cover_url}
              alt={item.title}
              className="h-full w-full object-cover"
            />
          </div>
        );
      }
    }

    return null;
  };

  // Source / Avatar Logic (Left Column)
  const renderAvatar = () => {
    // Priority: Source Icon -> Type Icon
    if (item.website?.favicon_url) {
      return (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background p-1.5">
          <img
            src={item.website?.favicon_url}
            alt="icon"
            className="w-full h-full object-contain"
          />
        </div>
      );
    }
    return (
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${typeConfig.bg}`}
      >
        <TypeIcon size={18} className={typeConfig.color} />
      </div>
    );
  };

  // Data processing
  const sourceName = itemFormat === "website"
    ? getWebsiteHostname(item.url)
    : getSourceName(item.url || "");
  const folderName = getFolderNameById(item.folder_id ?? "", folders);
  const createdAtLabel = formatRelativeTime(item.created_at) || "未知时间";
  const createdAtTitle = formatFullDateTime(item.created_at) || createdAtLabel;

  return (
    <div
      data-slot="item-row"
      data-active={isActive}
      onClick={onClick}
      className={`
      group relative flex cursor-pointer items-start gap-3 rounded-lg px-3 py-3 transition-colors duration-150
      ${
        isActive
          ? "bg-accent/45"
          : "hover:bg-sidebar-accent"
      }
    `}
    >
      {!selectionMode && <ActionOverlay />}
      {selectionMode && (
        <div
          className="pointer-events-none absolute right-3 top-3 z-40 flex size-7 items-center justify-center"
        >
          <span
            className={`flex size-5 items-center justify-center rounded-md border-[1.5px] transition-colors duration-200 ${
              isSelected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border/70 bg-background/90 text-transparent"
            }`}
          >
            <Check className="size-3" strokeWidth={2.6} />
          </span>
        </div>
      )}
      {/* COL 1: Left Anchor (Logo/Avatar) */}
      <div className="hidden pt-0.5 sm:block">{renderAvatar()}</div>

      {/* COL 2: Middle Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Meta Row: Source · Date · Status */}
        <div className="flex items-center gap-2 text-[11px] font-medium leading-none text-muted-foreground/75">
          {/* Mobile Avatar Fallback */}
          <div className="sm:hidden w-4 h-4 rounded-full bg-muted flex items-center justify-center overflow-hidden">
            {item.website?.favicon_url ? (
              <img src={item.website.favicon_url} className="w-full h-full" />
            ) : (
              <TypeIcon size={10} />
            )}
          </div>

          <span className="max-w-[200px] truncate">
            {sourceName}
          </span>
          <span className="text-muted-foreground/40">·</span>
          <span
            title={createdAtTitle}
          >
            {createdAtLabel}
          </span>
        </div>

        {/* Title */}
        <h3
          className={`truncate pr-2 text-sm font-semibold leading-5 ${
            isActive
              ? "text-primary"
              : "text-foreground"
          }`}
        >
          {item?.title || "Untitled"}
        </h3>

        {/* Description */}
        {notePreview?.trim() ? (
          <p className="line-clamp-2 text-xs leading-5 text-muted-foreground/80">
            {notePreview}
          </p>
        ) : null}

        {/* Footer Row: Folder & Tags */}
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* Folder Badge */}

          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
            {folderName !== '未分类' ? <Folder size={14} /> : <Inbox size={14} />}
            <span className="truncate max-w-[120px]">{folderName}</span>
          </div>

          {/* Tags */}
          {itemTags.length > 0 && (
            <>
              <span className="h-3 w-px shrink-0 bg-border/70" aria-hidden="true" />
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {itemTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex min-w-0 max-w-[72px] items-center overflow-hidden rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground"
                    title={`#${tag}`}
                  >
                    <span className="truncate">#{tag}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* COL 3: Right Thumbnail */}
      {renderThumbnail()}
    </div>
  );
});
